import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useModal } from '../context/ModalContext';
import api from '../utils/axiosInstance';

export default function ForgotPassword() {
  const [step, setStep] = useState(1); // 1=email, 2=otp+newPassword
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const modal = useModal();

  const handleSendOtp = async () => {
    if (!email) return modal.alert('Please enter your email.', 'warning');

    setLoading(true);
    try {
      const res = await api.post('/api/auth/forgot-password', { email });
      if (res.data.success) {
        modal.alert('Reset code sent to your email.', 'success');
        setStep(2);
      } else {
        modal.alert(res.data.message || 'Failed to send reset code.', 'error');
      }
    } catch (err) {
      modal.alert(err.response?.data?.message || 'Something went wrong.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!otp || !newPassword || !confirmPassword)
      return modal.alert('All fields are required.', 'warning');
    if (newPassword.length < 6)
      return modal.alert('Password must be at least 6 characters.', 'warning');
    if (newPassword !== confirmPassword)
      return modal.alert('Passwords do not match.', 'warning');

    setLoading(true);
    try {
      const res = await api.post('/api/auth/reset-password', {
        email,
        otp,
        newPassword,
      });
      if (res.data.success) {
        modal.alert('Password reset successful! Please log in.', 'success');
        navigate('/login');
      } else {
        modal.alert(res.data.message || 'Reset failed.', 'error');
      }
    } catch (err) {
      modal.alert(err.response?.data?.message || 'Something went wrong.', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      if (step === 1) handleSendOtp();
      else handleResetPassword();
    }
  };

  return (
    <div className="auth-page flex min-h-screen items-center justify-center p-4">
      <div className="auth-shell flex w-full max-w-[800px] flex-col overflow-hidden bg-surface-card md:flex-row">

        {/* LEFT: Form */}
        <div className="w-full md:flex-1 p-8 flex flex-col justify-center">
          <div className="flex items-center mb-8">
            <img
              src="/walytic-logo-blue.png"
              alt="Walytic"
              className="h-8 object-contain dark:hidden"
            />
            <img
              src="/walytic-logo-white.png"
              alt="Walytic"
              className="h-8 object-contain hidden dark:block"
            />
          </div>

          <h2 className="text-2xl font-bold text-ink-primary mb-1">
            {step === 1 ? 'Forgot Password' : 'Reset Password'}
          </h2>
          <p className="text-ink-muted text-sm mb-6">
            {step === 1
              ? 'Enter your email and we\'ll send you a reset code.'
              : 'Enter the code sent to your email and set a new password.'}
          </p>

          {step === 1 ? (
            <>
              <div className="input-group mb-6">
                <label className="input-label">Email</label>
                <input
                  type="email"
                  className="input"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  onKeyDown={handleKeyDown}
                />
              </div>

              <button
                className="btn-primary w-full mb-4"
                onClick={handleSendOtp}
                disabled={loading}
              >
                {loading ? 'Sending...' : 'Send Reset Code'}
              </button>
            </>
          ) : (
            <>
              <div className="input-group mb-4">
                <label className="input-label">Reset Code</label>
                <input
                  type="text"
                  className="input"
                  placeholder="Enter 6-digit code"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  onKeyDown={handleKeyDown}
                  maxLength={6}
                />
              </div>

              <div className="input-group mb-4">
                <label className="input-label">New Password</label>
                <input
                  type="password"
                  className="input"
                  placeholder="Enter new password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  onKeyDown={handleKeyDown}
                />
              </div>

              <div className="input-group mb-6">
                <label className="input-label">Confirm Password</label>
                <input
                  type="password"
                  className="input"
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  onKeyDown={handleKeyDown}
                />
              </div>

              <button
                className="btn-primary w-full mb-4"
                onClick={handleResetPassword}
                disabled={loading}
              >
                {loading ? 'Resetting...' : 'Reset Password'}
              </button>

              <button
                className="text-sm text-ink-muted hover:text-brand mb-2 w-full text-center"
                onClick={() => handleSendOtp()}
                disabled={loading}
              >
                Resend code
              </button>
            </>
          )}

          <p className="text-center text-sm text-ink-muted">
            Remember your password?{' '}
            <button onClick={() => navigate('/login')} className="text-brand font-semibold hover:underline">
              Sign in
            </button>
          </p>
        </div>

        {/* RIGHT: Info Panel */}
        <div className="enterprise-auth-panel hidden flex-1 flex-col items-center justify-center p-8 text-white md:flex">
          <h3 className="font-bold text-2xl mb-4">Reset your password</h3>
          <p className="text-center text-white/90 text-sm leading-relaxed">
            We'll send a 6-digit code to your registered email. Use it to set a new password and regain access to your account.
          </p>
        </div>
      </div>
    </div>
  );
}
